import React, { useContext, useEffect, useState } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import { AuthContext } from "./AuthProvider";
import { useTranslation } from "react-i18next";
import i18n from "./i18n";
import Layout from "./layouts/Layout";
import { PermissionScopeProvider, STAFF_SCOPES } from "./authz/PermissionScope";
import PublicLayout from "./layouts/PublicLayout";
import PublicParentMeetingConfirmation from "./pages/public/PublicParentMeetingConfirmation";
import "./index.css";

import ErrorPage from "./components/ErrorPage";
import Home from "./pages/Home";
import Dashboard from "./pages/Dashboard";
import Roles from "./pages/roles";
import { Users, UserNew, UserDetails } from "./pages/users";
import Teachers from "./pages/SchoolStructure/teachers/Teachers";
import TeacherNew from "./pages/SchoolStructure/teachers/TeacherNew";
import TeacherDetails from "./pages/SchoolStructure/teachers/TeacherDetails";
import TeacherEdit from "./pages/SchoolStructure/teachers/TeacherEdit";
import Classrooms from "./pages/SchoolStructure/classrooms/Classrooms";
import SchoolOverview from "./pages/SchoolOverview";
import PlacementOverview from "./pages/PlacementOverview";
import FuturePlanning from "./pages/planning/FuturePlanning";
import SignalsOverview from "./pages/SignalsOverview";
import ToolsOverview from "./pages/ToolsOverview";
import AdministrationOverview from "./pages/AdministrationOverview";
import AdminBackups from "./pages/AdminBackups";
import AdminOverview from "./pages/AdminOverview";
import ClassroomNew from "./pages/SchoolStructure/classrooms/ClassroomNew";
import SchoolYears from "./pages/SchoolStructure/years/SchoolYears";
import SchoolYearNew from "./pages/SchoolStructure/years/SchoolYearNew";
import SchoolYearDetails from "./pages/SchoolStructure/years/SchoolYearDetails";
import SchoolYearEdit from "./pages/SchoolStructure/years/SchoolYearEdit";
import Subjects from "./pages/SchoolStructure/subjects/Subjects";
import SchoolAttendanceAdmin from "./pages/SchoolAttendanceAdmin";
import ClassroomDetails from "./pages/SchoolStructure/classrooms/ClassroomDetails";
import ClassroomEdit from "./pages/SchoolStructure/classrooms/ClassroomEdit";
import Profile from "./pages/Profile";
import FamilyOverview from "./pages/FamilyOverview";
import Families from "./pages/Families";
import FamilyNew from "./pages/FamilyNew";
import FamilyDetails from "./pages/FamilyDetails";
import FamilyEdit from "./pages/FamilyEdit";
import BankAccountLookup from "./pages/BankAccountLookup";
import FinanceOverview from "./pages/finance/FinanceOverview";
import FinanceSettings from "./pages/finance/FinanceSettings";
import FinanceImportLog from "./pages/finance/FinanceImportLog";
import FinanceContributions from "./pages/finance/FinanceContributions";
import ParentDetails from "./pages/ParentDetails";
import ParentEdit from "./pages/ParentEdit";
import Parents from "./pages/Parents";
import ParentNew from "./pages/ParentNew";
import ChildrenOverview from "./pages/ChildrenOverview";
import ChildNew from "./pages/Children/new/ChildNew";
import ChildDetails from "./pages/Children/details/ChildDetails";
import ChildEdit from "./pages/Children/edit/ChildEdit";
import WaitingList from "./pages/WaitingList";
import Reports from "./pages/Reports";
import ReportsFamilies from "./pages/ReportsFamilies";
import ReportsNewFamilies from "./pages/ReportsNewFamilies";
import ClassroomOverview from "./pages/ClassroomOverview";
import ChildrenUnassigned from "./pages/ChildrenUnassigned";
import DuoRegistrations from "./pages/DuoRegistrations";
import ChildrenWaitingWithClass from "./pages/ChildrenWaitingWithClass";
import CurrentSchoolYearSignals from "./pages/CurrentSchoolYearSignals";
import ClassroomMigrate from "./pages/ClassroomMigrate";
import RegisterPage from "./pages/RegisterPage";
import ActivateAccountPage from "./pages/ActivateAccountPage";
import LoginRedirect from "./pages/LoginRedirect";
import MutationRequests from "./pages/Administration/Management/MutationRequests";
import MutationProcessingPage from "./pages/Administration/Management/MutationProcessing";
import FamilyApplicationsAdmin from "./pages/FamilyApplicationsAdmin";
import FamilySignup from "./pages/FamilySignup";
import ReportsStudents from "./pages/ReportsStudents";
import ReportsPrivacy from "./pages/ReportsPrivacy";
import ReportsAttendance from "./pages/ReportsAttendance";
import ReportsAlumni from "./pages/ReportsAlumni";
import ReportsAnalytics from "./pages/ReportsAnalytics";
import OutingsReport from "./pages/reports/OutingsReport";
import ReportBuilderListPage from "./features/reportBuilder/ReportBuilderListPage";
import ReportBuilderPage from "./features/reportBuilder/ReportBuilderPage";
import ReportViewPage from "./features/reportBuilder/ReportViewPage";
import AdminNotifications from "./pages/AdminNotifications";
import AdminMaintenance from "./pages/AdminMaintenance";
import AdminMaintenanceMailchimp from "./pages/AdminMaintenanceMailchimp";
import AdminEmailValidationDomains from "./pages/AdminEmailValidationDomains";
import AdminSchoolSettings from "./pages/AdminSchoolSettings";
import SchoolCalendar from "./pages/SchoolCalendar";
import AdminLogs from "./pages/AdminLogs";
import Directory from "./pages/Directory";
import NoctisObjectTypes from "./pages/noctis/NoctisObjectTypes";
import NoctisOverview from "./pages/noctis/NoctisOverview";
import NoctisConsole from "./pages/noctis/NoctisConsole";
import NoctisReferenceStatus from "./pages/noctis/NoctisReferenceStatus";
import NoctisEventsLog from "./pages/noctis/NoctisEventsLog";
import NoctisOutboxQueue from "./pages/noctis/NoctisOutboxQueue";
import GiteaRecentIssues from "./pages/GiteaRecentIssues";
import SchoolTasksAdmin from "./pages/SchoolTasksAdmin";
import { RequireSchoolTasks } from "./features/featureFlags";
import { LumenHistoryRecorder } from "./components/navigation/BackButton";
import { UnsavedChangesGuardProvider } from "./components/navigation/UnsavedChangesGuard";

import { createParentPortalRoutes } from "./portals/parent/routes";
import { createTeacherPortalRoutes } from "./portals/teacher/routes";
import RequireCan from "./authz/RequireCan";
import RequireAnyCan from "./authz/RequireAnyCan";
import LandingGate from "./authz/LandingGate";
import { STAFF_PORTAL_ENTRY_CHECKS } from "./authz/staffPortalAccess";
import { useCan } from "./authz/useCan";
import { useSupportViewAs } from "./supportViewAs/SupportViewAsProvider";
import { classroomOverviewRouteChecks } from "./authz/featurePermissionContracts";

const anyActions = (resource, actions) => actions.map((action) => ({ resource, action }));
const canListOrRead = (resource) => anyActions(resource, ["list", "read", "view"]);
const canCrudOrManage = (resource) => anyActions(resource, ["crud", "manage"]);
const canListOrView = (resource) => anyActions(resource, ["list", "view"]);
const maintenanceRouteChecks = [
  { resource: "outing.info", action: "manage" },
  { resource: "newsletter.recipients", action: "status" },
  { resource: "newsletter.recipients", action: "export" },
  { resource: "newsletter.recipients", action: "sync" },
  { resource: "email.validation", action: "manage" },
];
const schoolCalendarRouteChecks = [
  { resource: "school.calendar", action: "read" },
  { resource: "school.calendar", action: "manage" },
];
const schoolTasksRouteChecks = [
  { resource: "school.tasks", action: "view" },
  { resource: "school.tasks", action: "manage" },
  { resource: "school.task-signups", action: "view" },
  { resource: "school.task-signups", action: "manage" },
  { resource: "school.cleaning", action: "view" },
  { resource: "school.cleaning", action: "manage" },
];
const myAgendaActivitiesRouteChecks = [
  { resource: "my.agenda-activities", action: "view" },
  { resource: "classroom.calendar", action: "read" },
  { resource: "classroom.calendar", action: "manage" },
  { resource: "outing.info", action: "read" },
  { resource: "outing.info", action: "manage" },
  { resource: "reports.outings", action: "view" },
];
const mailchimpMaintenanceRouteChecks = [
  { resource: "newsletter.recipients", action: "status" },
  { resource: "newsletter.recipients", action: "export" },
  { resource: "newsletter.recipients", action: "sync" },
];
const myEnvironmentRouteChecks = [
  { resource: "my.environment", action: "view" },
  { resource: "my.teacher-details", action: "view" },
  { resource: "my.classrooms", action: "view" },
  { resource: "my.parent-meetings", action: "view" },
  ...myAgendaActivitiesRouteChecks,
];
const myClassroomsRouteChecks = [
  { resource: "my.classrooms", action: "view" },
];
const myParentMeetingsRouteChecks = [
  { resource: "my.parent-meetings", action: "view" },
];
const myOutingsRouteChecks = [
  ...myAgendaActivitiesRouteChecks,
];
const schoolOverviewRouteChecks = [
  { resource: "school", action: "read" },
  { resource: "school", action: "manage" },
];
const schoolRootRouteChecks = schoolOverviewRouteChecks;
const placementRouteChecks = [
  { resource: "school.placement-overview", action: "view" },
];

const futurePlanningRouteChecks = [
  { resource: "school.future-planning", action: "view" },
  { resource: "school.future-planning", action: "manage" },
];
const signalsRouteChecks = [
  { resource: "signals.unassigned-children", action: "view" },
  { resource: "signals.waiting-list-conflicts", action: "view" },
  { resource: "signals.current-school-year", action: "view" },
];
const toolsRouteChecks = [
  { resource: "directory", action: "manage" },
];
const financeRouteChecks = [
  { resource: "finance.root", action: "view" },
  { resource: "finance.settings", action: "view" },
  { resource: "finance.contributions", action: "view" },
  { resource: "families.bankaccounts", action: "search" },
];
const administrationRouteChecks = [
  ...canCrudOrManage("school.mutations"),
  { resource: "administration.mutation-processing", action: "read" },
  { resource: "alumni", action: "read" },
  { resource: "alumni", action: "crud" },
  { resource: "school.settings", action: "read" },
  { resource: "school.settings", action: "update" },
  { resource: "school.links", action: "manage" },
];
const schoolSettingsRouteChecks = [
  { resource: "school.settings", action: "read" },
  { resource: "school.settings", action: "update" },
  { resource: "school.links", action: "manage" },
];
const mutationProcessingPolicyRouteChecks = [
  { resource: "administration.mutation-processing", action: "read" },
];
const mutationRequestsRouteChecks = [
  ...canCrudOrManage("school.mutations"),
];

const getSystemPrefersDark = () => {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
    return false;
  }
  return window.matchMedia("(prefers-color-scheme: dark)").matches;
};

function SupportViewAsBanner() {
  const supportViewAs = useSupportViewAs();
  if (!supportViewAs?.active || !supportViewAs?.target) return null;

  const target = supportViewAs.target;
  const label = target.label || target.email || target.userId || "gebruiker";
  const issue = target.issueNumber ? `#${target.issueNumber}` : null;

  return (
    <>
      <div className="fixed inset-x-0 top-0 z-[10001] border-b border-amber-300 bg-amber-100 px-4 py-2 text-sm text-amber-950 shadow-sm dark:border-amber-700 dark:bg-amber-950 dark:text-amber-100">
        <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-3">
          <div>
            <span className="font-semibold">View-as read-only:</span>{" "}
            <span>{label}</span>
            {issue ? <span className="ml-2 text-amber-800 dark:text-amber-200">via Gitea {issue}</span> : null}
            <span className="ml-2 text-amber-800 dark:text-amber-200">
              Menu en rechten volgen deze gebruiker.
            </span>
          </div>
          <button
            type="button"
            onClick={supportViewAs.stop}
            className="rounded-lg border border-amber-400 bg-white px-3 py-1 text-xs font-semibold text-amber-900 shadow-sm hover:bg-amber-50 dark:border-amber-700 dark:bg-amber-900 dark:text-amber-100 dark:hover:bg-amber-800"
          >
            Stop view-as
          </button>
        </div>
      </div>
      <div className="h-12" aria-hidden="true" />
    </>
  );
}

function App() {
  const { t } = useTranslation();
  const auth = useContext(AuthContext);
  const [darkMode, setDarkMode] = useState(getSystemPrefersDark);
  const canSeeAdminRoot = useCan("admin", "view");
  const canManageUsers = useCan("admin.users", "manage");
  const canManageNotifications = useCan("admin.notifications", "manage");
  const canManageBackupsPermission = useCan("admin.backups", "manage");
  const canViewLogs = useCan("admin.audit", "read");
  const canViewAdmin =
    canSeeAdminRoot &&
    (canManageUsers ||
      canManageNotifications ||
      canManageBackupsPermission ||
      canViewLogs);

  useEffect(() => {
    if (auth?.userInfo?.preferred_language) {
      i18n.changeLanguage(auth.userInfo.preferred_language);
    }
  }, [auth]);

  useEffect(() => {
    if (typeof window !== "undefined" && typeof window.matchMedia === "function") {
      const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
      const handleChange = (event) => setDarkMode(event.matches);
      if (typeof mediaQuery.addEventListener === "function") {
        mediaQuery.addEventListener("change", handleChange);
        return () => mediaQuery.removeEventListener("change", handleChange);
      }
      mediaQuery.addListener(handleChange);
      return () => {
        if (typeof mediaQuery.removeListener === "function") {
          mediaQuery.removeListener(handleChange);
        }
      };
    }
  }, []);

  useEffect(() => {
    if (typeof document === "undefined") return;
    document.documentElement.classList.toggle("dark", !!darkMode);
    document.body.classList.toggle("dark", !!darkMode);
  }, [darkMode]);

  return (
    <Router>
      <LumenHistoryRecorder />
      <UnsavedChangesGuardProvider>
      <SupportViewAsBanner />
      <Routes>
        <Route path="/" element={<Home />} />


        {/* Public */}
        <Route element={<PublicLayout />}>
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/activate-account/:token" element={<ActivateAccountPage />} />
          <Route path="/signup" element={<FamilySignup />} />
          <Route path="/aanmelden" element={<FamilySignup />} />
          <Route path="/parent-meetings/confirm/:token" element={<PublicParentMeetingConfirmation />} />
        </Route>

        {/* Parent Portal (eigen logic, staat los van staf-permissions) */}
        {createParentPortalRoutes()}

        {/* App shell */}
        {/* Staff shell: only school-wide (ANY) and own-work (OWN) grants count here, never a parent's RELATED access. */}
        <Route element={<PermissionScopeProvider scopes={STAFF_SCOPES}><Layout darkMode={darkMode} /></PermissionScopeProvider>}>
          {/* Dashboard */}
          <Route
            path="/dashboard"
            element={
              <RequireCan resource="dashboard" action="view" fallback={<Navigate to="/403" />}>
                <Dashboard />
              </RequireCan>
            }
          />
          {createTeacherPortalRoutes({
            myEnvironmentRouteChecks,
            myClassroomsRouteChecks,
            myParentMeetingsRouteChecks,
            myOutingsRouteChecks,
            classroomManagementRouteChecks: [
              { resource: "my.parent-meetings", action: "manage" },
              ...canCrudOrManage("school.classrooms"),
              { resource: "school.classrooms.overview", action: "manage" },
            ],
            schoolRootRouteChecks,
          })}
          <Route
            path="/directory"
            element={
              <RequireCan resource="directory" action="view" fallback={<Navigate to="/403" />}>
                <Directory />
              </RequireCan>
            }
          />

          {/* Admin */}
          <Route
            path="/admin"
            element={
              canViewAdmin ? <AdminOverview /> : <Navigate to="/403" />
            }
          />
          <Route
            path="/admin/notifications"
            element={
              <RequireCan resource="admin" action="view" fallback={<Navigate to="/403" />}>
                <RequireAnyCan
                  checks={[
                    { resource: "admin.notifications", action: "manage" },
                    { resource: "admin.notifications", action: "send" },
                  ]}
                >
                  <AdminNotifications />
                </RequireAnyCan>
              </RequireCan>
            }
          />
          <Route
            path="/admin/logs"
            element={
              <RequireCan resource="admin.audit" action="read" fallback={<Navigate to="/403" />}>
                <AdminLogs />
              </RequireCan>
            }
          />

          {/* Maintenance (top-level, but permissioned) */}
          <Route
            path="/maintenance"
            element={
              <RequireAnyCan
                checks={maintenanceRouteChecks}
              >
                <AdminMaintenance />
              </RequireAnyCan>
            }
          />
          <Route
            path="/maintenance/mailchimp"
            element={<Navigate to="/integrations/mailchimp" replace />}
          />
          <Route
            path="/maintenance/email-validation/domains"
            element={
              <RequireCan resource="email.validation" action="manage" fallback={<Navigate to="/403" />}>
                <AdminEmailValidationDomains />
              </RequireCan>
            }
          />
          <Route
            path="/maintenance/school-calendar"
            element={<Navigate to="/school/school-calendar" replace />}
          />
          <Route
            path="/integrations/mailchimp"
            element={
              <RequireAnyCan
                checks={mailchimpMaintenanceRouteChecks}
              >
                <AdminMaintenanceMailchimp />
              </RequireAnyCan>
            }
          />
          <Route
            path="/noctis"
            element={
              <RequireCan
                resource="integration.noctis.object-types"
                action="view"
                fallback={<Navigate to="/403" />}
              >
                <NoctisOverview />
              </RequireCan>
            }
          />
          <Route
            path="/noctis/object-types"
            element={
              <RequireCan
                resource="integration.noctis.object-types"
                action="view"
                fallback={<Navigate to="/403" />}
              >
                <NoctisObjectTypes />
              </RequireCan>
            }
          />
          <Route
            path="/noctis/console"
            element={
              <RequireCan
                resource="integration.noctis.object-types"
                action="view"
                fallback={<Navigate to="/403" />}
              >
                <NoctisConsole />
              </RequireCan>
            }
          />
          <Route
            path="/noctis/reference-status"
            element={
              <RequireCan
                resource="integration.noctis.object-types"
                action="view"
                fallback={<Navigate to="/403" />}
              >
                <NoctisReferenceStatus />
              </RequireCan>
            }
          />
          <Route
            path="/noctis/events"
            element={
              <RequireCan
                resource="integration.noctis.object-types"
                action="view"
                fallback={<Navigate to="/403" />}
              >
                <NoctisEventsLog />
              </RequireCan>
            }
          />
          <Route
            path="/noctis/queue"
            element={
              <RequireCan
                resource="integration.noctis.object-types"
                action="view"
                fallback={<Navigate to="/403" />}
              >
                <NoctisOutboxQueue />
              </RequireCan>
            }
          />
          <Route
            path="/gitea"
            element={
              <RequireAnyCan
                checks={[
                  { resource: "integration.gitea.issues", action: "read" },
                  { resource: "integration.gitea.issues", action: "read" },
                ]}
              >
                <GiteaRecentIssues />
              </RequireAnyCan>
            }
          />
          <Route
            path="/admin/users"
            element={
              <RequireCan resource="admin.users" action="manage" fallback={<Navigate to="/403" />}>
                <Users />
              </RequireCan>
            }
          />
          <Route
            path="/admin/users/new"
            element={
              <RequireCan resource="admin.users" action="manage" fallback={<Navigate to="/403" />}>
                <UserNew />
              </RequireCan>
            }
          />
          <Route
            path="/admin/users/:id"
            element={
              <RequireCan resource="admin.users" action="manage" fallback={<Navigate to="/403" />}>
                <UserDetails />
              </RequireCan>
            }
          />
          <Route
            path="/admin/roles"
            element={
              <RequireCan resource="admin.users" action="manage" fallback={<Navigate to="/403" />}>
                <Roles />
              </RequireCan>
            }
          />
          <Route
            path="/admin/manage/school"
            element={
              <RequireAnyCan checks={schoolSettingsRouteChecks} fallback={<Navigate to="/403" />}>
                <AdminSchoolSettings />
              </RequireAnyCan>
            }
          />
          <Route
            path="/admin/manage/school/:section"
            element={
              <RequireAnyCan checks={schoolSettingsRouteChecks} fallback={<Navigate to="/403" />}>
                <AdminSchoolSettings />
              </RequireAnyCan>
            }
          />

          {/* Family (admin only in jouw policy) */}
          <Route
            path="/family"
            element={
              <RequireAnyCan
                checks={[
                  { resource: "families", action: "list" },
                  { resource: "families", action: "list" },
                  { resource: "families", action: "read" },
                  { resource: "families", action: "read" },
                  { resource: "family.parents", action: "list" },
                  { resource: "family.parents", action: "read" },
                  { resource: "family.children", action: "list" },
                  { resource: "family.children", action: "read" },
                ]}
              >
                <FamilyOverview />
              </RequireAnyCan>
            }
          />
          <Route path="/family/families" element={
            <RequireAnyCan checks={[
              { resource: "families", action: "list" },
              { resource: "families", action: "read" },
              { resource: "families", action: "read" },
            ]}>
              <Families />
            </RequireAnyCan>
          }/>
          <Route path="/family/parents" element={
            <RequireAnyCan
              checks={[
                { resource: "family.parents", action: "list" },
                { resource: "family.parents", action: "read" },
                { resource: "families", action: "list" },
                { resource: "families", action: "read" },
                { resource: "families", action: "read" },
              ]}
            >
              <Parents />
            </RequireAnyCan>
          }/>
          <Route path="/family/parents/new" element={
            <RequireAnyCan checks={[
              { resource: "family.parents", action: "crud" },
              { resource: "family.parents", action: "manage" },
            ]}>
              <ParentNew />
            </RequireAnyCan>
          }/>
          <Route path="/family/parent/:id/details" element={
            <RequireAnyCan
              checks={[
                { resource: "family.parents", action: "read" },
                { resource: "families", action: "read" },
                { resource: "families", action: "read" },
              ]}
            >
              <ParentDetails />
            </RequireAnyCan>
          }/>
          <Route path="/family/parent/:id/edit" element={
            <RequireAnyCan checks={[
              { resource: "family.parents", action: "crud" },
              { resource: "family.parents", action: "manage" },
            ]}>
              <ParentEdit />
            </RequireAnyCan>
          }/>
          <Route path="/family/new" element={
            <RequireAnyCan checks={[{ resource: "families", action: "crud" }, { resource: "families", action: "crud" }]}>
              <FamilyNew />
            </RequireAnyCan>
          }/>
          <Route path="/family/:id/details" element={
            <RequireAnyCan checks={[{ resource: "families", action: "read" }, { resource: "families", action: "read" }]}>
              <FamilyDetails />
            </RequireAnyCan>
          }/>
          <Route path="/family/:id/edit" element={
            <RequireAnyCan checks={[{ resource: "families", action: "crud" }, { resource: "families", action: "crud" }]}>
              <FamilyEdit />
            </RequireAnyCan>
          }/>
          {/* De IBAN-zoeker staat onder Financieel; het oude adres blijft werken voor bladwijzers. */}
          <Route path="/family/bank-account-lookup" element={<Navigate to="/finance/bank-account-lookup" replace />}/>
          <Route path="/family/children" element={
            <RequireAnyCan checks={[
              { resource: "family.children", action: "list" },
              { resource: "family.children", action: "read" },
            ]}>
              <ChildrenOverview />
            </RequireAnyCan>
          }/>
          <Route path="/family/children/new" element={
            <RequireAnyCan checks={[
              { resource: "family.children", action: "crud" },
              { resource: "family.children", action: "manage" },
            ]}>
              <ChildNew />
            </RequireAnyCan>
          }/>
          <Route path="/family/children/:id/details" element={
            <RequireCan resource="family.children" action="read" fallback={<Navigate to="/403" />}>
              <ChildDetails />
            </RequireCan>
          }/>
          <Route path="/family/children/:id/edit" element={
            <RequireAnyCan checks={[
              { resource: "family.children", action: "crud" },
              { resource: "family.children", action: "manage" },
            ]}>
              <ChildEdit />
            </RequireAnyCan>
          }/>
          <Route path="/family/directory" element={
              <RequireCan resource="directory" action="manage" fallback={<Navigate to="/403" />}>
                <Directory />
              </RequireCan>
          }/>

          {/* New menu root pages */}
          <Route
            path="/plaatsing"
            element={
              <RequireAnyCan checks={placementRouteChecks} fallback={<Navigate to="/403" />}>
                <PlacementOverview />
              </RequireAnyCan>
            }
          />
          <Route
            path="/placement"
            element={
              <RequireAnyCan checks={placementRouteChecks} fallback={<Navigate to="/403" />}>
                <PlacementOverview />
              </RequireAnyCan>
            }
          />
          <Route
            path="/plaatsing/toekomstige-planning"
            element={
              <RequireAnyCan checks={futurePlanningRouteChecks} fallback={<Navigate to="/403" />}>
                <FuturePlanning />
              </RequireAnyCan>
            }
          />
          <Route
            path="/signaleringen"
            element={
              <RequireAnyCan checks={signalsRouteChecks} fallback={<Navigate to="/403" />}>
                <SignalsOverview />
              </RequireAnyCan>
            }
          />
          <Route
            path="/finance"
            element={
              <RequireAnyCan checks={financeRouteChecks} fallback={<Navigate to="/403" />}>
                <FinanceOverview />
              </RequireAnyCan>
            }
          />
          <Route path="/finance/students" element={<Navigate to="/finance/payments?view=kind" replace />}/>
          <Route path="/finance/payments" element={
            <RequireAnyCan
              checks={[
                { resource: "finance.contributions", action: "view" },
                { resource: "finance.reports.students", action: "view" },
              ]}
            >
              <FinanceContributions />
            </RequireAnyCan>
          }/>
          <Route path="/finance/contributions" element={<Navigate to="/finance/payments" replace />}/>
          <Route path="/finance/settings" element={
            <RequireCan resource="finance.settings" action="view">
              <FinanceSettings />
            </RequireCan>
          }/>
          <Route path="/finance/imports" element={
            <RequireAnyCan
              checks={[
                { resource: "finance.contributions", action: "view" },
                { resource: "finance.settings", action: "view" },
              ]}
            >
              <FinanceImportLog />
            </RequireAnyCan>
          }/>
          {/* e-Boekhouden is an integration: same page, reachable from Integrations. */}
          <Route path="/integrations/eboekhouden" element={
            <RequireCan resource="finance.settings" action="view">
              <FinanceSettings />
            </RequireCan>
          }/>
          <Route path="/finance/bank-account-lookup" element={
            <RequireCan resource="families.bankaccounts" action="search">
              <BankAccountLookup />
            </RequireCan>
          }/>
          <Route
            path="/tools"
            element={
              <RequireAnyCan checks={toolsRouteChecks} fallback={<Navigate to="/403" />}>
                <ToolsOverview />
              </RequireAnyCan>
            }
          />
          <Route
            path="/administratie"
            element={
              <RequireAnyCan checks={administrationRouteChecks} fallback={<Navigate to="/403" />}>
                <AdministrationOverview />
              </RequireAnyCan>
            }
          />

          {/* Enrollment manager */}
          <Route
            path="/school"
            element={
              <RequireAnyCan checks={schoolOverviewRouteChecks} fallback={<Navigate to="/403" />}>
                <SchoolOverview />
              </RequireAnyCan>
            }
          />
          <Route
            path="/school/school-calendar"
            element={
              <RequireAnyCan checks={schoolCalendarRouteChecks} requiredChecks={schoolRootRouteChecks} fallback={<Navigate to="/403" />}>
                <SchoolCalendar />
              </RequireAnyCan>
            }
          />
          <Route path="/school/school-tasks" element={<RequireSchoolTasks fallback={<ErrorPage status={404} message={t("error.404.message")} onRetry={() => (window.location.href = "/")} />}><RequireAnyCan checks={schoolTasksRouteChecks} fallback={<Navigate to="/403"/>}><SchoolTasksAdmin/></RequireAnyCan></RequireSchoolTasks>} />
          <Route
            path="/school/waiting-list"
            element={
              <RequireAnyCan checks={[...canListOrView("school.waiting-list"), { resource: "school.waiting-list", action: "assign" }, { resource: "school.waiting-list", action: "assign" }]} requiredChecks={schoolRootRouteChecks}>
                <WaitingList />
              </RequireAnyCan>
            }
          />
          <Route
            path="/school/mutations"
            element={
              <RequireAnyCan checks={mutationRequestsRouteChecks} requiredChecks={schoolRootRouteChecks}>
                <MutationRequests />
              </RequireAnyCan>
            }
          />
          <Route
            path="/administratie/mutaties"
            element={
              <RequireAnyCan checks={mutationRequestsRouteChecks}>
                <MutationRequests />
              </RequireAnyCan>
            }
          />
          <Route
            path="/admin/manage/mutation-processing"
            element={
              <RequireAnyCan checks={mutationProcessingPolicyRouteChecks} fallback={<Navigate to="/403" />}>
                <MutationProcessingPage />
              </RequireAnyCan>
            }
          />
          <Route path="/admin/mutation-processing" element={<Navigate to="/admin/manage/mutation-processing" replace />} />
          <Route
            path="/school/applications"
            element={
              <RequireAnyCan checks={canCrudOrManage("school.applications")} requiredChecks={schoolRootRouteChecks}>
                <FamilyApplicationsAdmin />
              </RequireAnyCan>
            }
          />
          <Route
            path="/school/attendance"
            element={
              <RequireAnyCan checks={schoolRootRouteChecks}>
                <RequireCan resource="school.attendance" action="view" fallback={<Navigate to="/403" />}>
                  <RequireCan resource="school.attendance" action="manage" fallback={<Navigate to="/403" />}>
                  <SchoolAttendanceAdmin />
                  </RequireCan>
                </RequireCan>
              </RequireAnyCan>
            }
          />

          {/* School years */}
          <Route
            path="/school/subjects"
            element={
              <RequireAnyCan checks={[...canListOrRead("school.subjects"), ...anyActions("school.subjects", ["create", "update", "delete"]), ...canCrudOrManage("school.subjects")]} requiredChecks={schoolRootRouteChecks}>
                <Subjects />
              </RequireAnyCan>
            }
          />

          <Route
            path="/school/years"
            element={
              <RequireAnyCan checks={canCrudOrManage("school.years")} requiredChecks={schoolRootRouteChecks}>
                <SchoolYears />
              </RequireAnyCan>
            }
          />
          <Route
            path="/school/years/new"
            element={
              <RequireAnyCan checks={canCrudOrManage("school.years")} requiredChecks={schoolRootRouteChecks}>
                <SchoolYearNew />
              </RequireAnyCan>
            }
          />
          <Route
            path="/school/years/:id/details"
            element={
              <RequireAnyCan checks={canCrudOrManage("school.years")} requiredChecks={schoolRootRouteChecks}>
                <SchoolYearDetails />
              </RequireAnyCan>
            }
          />
          <Route
            path="/school/years/:id/edit"
            element={
              <RequireAnyCan checks={canCrudOrManage("school.years")} requiredChecks={schoolRootRouteChecks}>
                <SchoolYearEdit />
              </RequireAnyCan>
            }
          />

          {/* Classrooms */}
          <Route
            path="/school/classrooms"
            element={
              <RequireAnyCan checks={[...canListOrRead("school.classrooms"), ...canCrudOrManage("school.classrooms")]} requiredChecks={schoolRootRouteChecks}>
                <Classrooms />
              </RequireAnyCan>
            }
          />
          <Route
            path="/school/classrooms/overview"
            element={
              <RequireAnyCan checks={classroomOverviewRouteChecks} requiredChecks={schoolRootRouteChecks}>
                <ClassroomOverview />
              </RequireAnyCan>
            }
          />
          <Route
            path="/school/classrooms/new"
            element={
              <RequireAnyCan checks={[...canCrudOrManage("school.classrooms"), { resource: "school.classrooms.overview", action: "manage" }]} requiredChecks={schoolRootRouteChecks}>
                <ClassroomNew />
              </RequireAnyCan>
            }
          />
          <Route
            path="/school/classrooms/:id/details"
            element={
              <RequireAnyCan checks={[...anyActions("school.classrooms", ["read", "view"]), ...canCrudOrManage("school.classrooms"), { resource: "school.classrooms.overview", action: "manage" }]} requiredChecks={schoolRootRouteChecks}>
                <ClassroomDetails />
              </RequireAnyCan>
            }
          />
          <Route
            path="/school/classrooms/:id/edit"
            element={
              <RequireAnyCan checks={[...canCrudOrManage("school.classrooms"), { resource: "school.classrooms.overview", action: "manage" }]} requiredChecks={schoolRootRouteChecks}>
                <ClassroomEdit />
              </RequireAnyCan>
            }
          />
          <Route
            path="/school/classrooms/:id/migrate"
            element={
              <RequireAnyCan checks={[...canCrudOrManage("school.classrooms"), { resource: "school.classrooms.overview", action: "manage" }]} requiredChecks={schoolRootRouteChecks}>
                <ClassroomMigrate />
              </RequireAnyCan>
            }
          />
          {/* Teachers */}
          <Route
            path="/school/teachers"
            element={
              <RequireAnyCan checks={[...canListOrRead("school.teachers"), { resource: "school.teachers", action: "linkSelf" }]} requiredChecks={schoolRootRouteChecks}>
                <Teachers />
              </RequireAnyCan>
            }
          />
          <Route
            path="/school/teachers/new"
            element={
              <RequireAnyCan checks={canCrudOrManage("school.teachers")} requiredChecks={schoolRootRouteChecks}>
                <TeacherNew />
              </RequireAnyCan>
            }
          />
          <Route
            path="/school/teachers/:id/details"
            element={
              <RequireAnyCan checks={canListOrRead("school.teachers")} requiredChecks={schoolRootRouteChecks}>
                <TeacherDetails />
              </RequireAnyCan>
            }
          />
          <Route
            path="/school/teachers/:id/edit"
            element={
              <RequireAnyCan checks={canCrudOrManage("school.teachers")} requiredChecks={schoolRootRouteChecks}>
                <TeacherEdit />
              </RequireAnyCan>
            }
          />

          {/* Backups */}
          <Route
            path="/admin/backups"
            element={
              <RequireCan resource="admin.backups" action="manage" fallback={<Navigate to="/403" />}>
                <AdminBackups />
              </RequireCan>
            }
          />

          {/* Reports */}
          <Route
            path="/reports"
            element={
              <RequireCan resource="reports.root" action="view" fallback={<Navigate to="/403" />}>
                <Reports />
              </RequireCan>
            }
          />
          <Route
            path="/reports/analytics"
            element={
              <RequireCan resource="reports.root" action="view" fallback={<Navigate to="/403" />}>
                <RequireCan resource="admin.analytics" action="read" fallback={<Navigate to="/403" />}>
                  <ReportsAnalytics />
                </RequireCan>
              </RequireCan>
            }
          />
          <Route
            path="/reports/families"
            element={
              <RequireCan resource="reports.families" action="view" fallback={<Navigate to="/403" />}>
                <ReportsFamilies />
              </RequireCan>
            }
          />
          <Route
            path="/reports/new-families"
            element={
              <RequireCan resource="reports.new-families" action="view" fallback={<Navigate to="/403" />}>
                <ReportsNewFamilies />
              </RequireCan>
            }
          />
          <Route
            path="/reports/builder"
            element={
              <RequireCan resource="reports.builder" action="view" fallback={<Navigate to="/403" />}>
                <ReportBuilderListPage />
              </RequireCan>
            }
          />
          <Route
            path="/reports/builder/new"
            element={
              <RequireCan resource="reports.builder" action="create" fallback={<Navigate to="/403" />}>
                <ReportBuilderPage />
              </RequireCan>
            }
          />
          <Route
            path="/reports/builder/:id"
            element={
              <RequireCan resource="reports.builder" action="view" fallback={<Navigate to="/403" />}>
                <ReportViewPage />
              </RequireCan>
            }
          />
          <Route
            path="/reports/builder/:id/edit"
            element={
              <RequireCan resource="reports.builder" action="create" fallback={<Navigate to="/403" />}>
                <ReportBuilderPage />
              </RequireCan>
            }
          />
          <Route
            path="/reports/students"
            element={
              <RequireCan
                resource="reports.students"           // <-- zelfde key als in Sidebar + pagina
                  action="view"
                  fallback={<Navigate to="/403" />}
                >
                <ReportsStudents />
              </RequireCan>
            }
          />
          <Route
            path="/reports/attendance"
            element={
              <RequireCan resource="reports.attendance" action="view" fallback={<Navigate to="/403" />}>
                <ReportsAttendance />
              </RequireCan>
            }
          />
          <Route
            path="/reports/outings"
            element={
              <RequireCan resource="reports.outings" action="view" fallback={<Navigate to="/403" />}>
                <OutingsReport />
              </RequireCan>
            }
          />
          <Route
            path="/reports/privacy"
            element={
              <RequireCan resource="reports.privacy" action="view" fallback={<Navigate to="/403" />}>
                <ReportsPrivacy />
              </RequireCan>
            }
          />
          <Route
            path="/reports/alumni"
            element={
              <RequireAnyCan checks={[
                { resource: "alumni", action: "read" },
                { resource: "alumni", action: "crud" },
              ]} fallback={<Navigate to="/403" />}>
                <ReportsAlumni />
              </RequireAnyCan>
            }
          />
          <Route
            path="/reports/unassigned-children"
            element={
              <RequireCan resource="signals.unassigned-children" action="view" fallback={<Navigate to="/403" />}>
                <ChildrenUnassigned />
              </RequireCan>
            }
          />
          <Route
            path="/reports/duo-registrations"
            element={
              <RequireCan resource="reports.duo-registrations" action="manage" fallback={<Navigate to="/403" />}>
                <DuoRegistrations />
              </RequireCan>
            }
          />
          <Route
            path="/reports/waiting-with-class"
            element={
              <RequireCan resource="signals.waiting-list-conflicts" action="view" fallback={<Navigate to="/403" />}>
                <ChildrenWaitingWithClass />
              </RequireCan>
            }
          />
          <Route
            path="/reports/current-school-year-conflicts"
            element={
              <RequireCan resource="signals.current-school-year" action="view" fallback={<Navigate to="/403" />}>
                <CurrentSchoolYearSignals />
              </RequireCan>
            }
          />

          {/* Profile (mag als je het docentenportaal in mag) */}
          <Route
            path="/profile"
            element={
              <RequireAnyCan checks={STAFF_PORTAL_ENTRY_CHECKS}>
                <Profile />
              </RequireAnyCan>
            }
          />

          {/* Error page */}
          <Route path="/error" element={<ErrorPage />} />
        </Route>

        {/* Errors & Auth */}
        <Route
          path="/403"
          element={
            <ErrorPage
              status={403}
              message={t("error.403.message")}
              onRetry={() => (window.location.href = "/")}
              showLogout
            />
          }
        />
        <Route path="/login" element={<LoginRedirect />} />
        <Route
          path="/unauthorized"
          element={
            <ErrorPage
              message={t("error.unauthorized")}
              onRetry={() => (window.location.href = "/login")}
              showLogout
            />
          }
        />
        <Route
          path="*"
          element={
            <ErrorPage
              status={404}
              message={t("error.404.message")}
              onRetry={() => (window.location.href = "/")}
            />
          }
        />
      </Routes>
      </UnsavedChangesGuardProvider>
    </Router>
  );
}

export default App;
